import { getEnvTenantId, isValidTenantId, normalizeTenantId } from "@/lib/tenantId";
import { getTenantId, setLocalTenantId } from "@/lib/storeManager";
import { createContext, ReactNode, useEffect, useState } from "react";

interface TenantContextType {
  tenantId: string | null;
  setTenantId: (id: string) => void;
  clearTenantId: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

const ENV_TENANT_ID = getEnvTenantId();

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantId, setTenantIdState] = useState<string | null>(() => {
    // 1. Env variable (always reliable)
    if (ENV_TENANT_ID) {
      return ENV_TENANT_ID;
    }
    // 2. localStorage (valid persisted session)
    const stored = normalizeTenantId(getTenantId());
    if (stored) {
      return stored;
    }
    // 3. URL first segment (skip known app route prefixes)
    const RESERVED_PREFIXES = new Set(["sd", "login"]);
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const fromUrl = pathParts.find(p => !RESERVED_PREFIXES.has(p.toLowerCase())) ?? null;
    return normalizeTenantId(fromUrl);
  });

  const setTenantIdHandler = (id: string) => {
    const normalized = normalizeTenantId(id);
    if (!normalized) return; // never save route names like "login"
    setLocalTenantId(normalized);
    setTenantIdState(normalized);
  };

  const clearTenantId = () => {
    localStorage.removeItem("tenantId");
    setTenantIdState(null);
  };

  // Sync valid tenantId to localStorage
  useEffect(() => {
    if (tenantId && isValidTenantId(tenantId)) {
      setLocalTenantId(tenantId);
    }
  }, [tenantId]);

  // Listen for popstate (browser back/forward)
  useEffect(() => {
    const handleUrlChange = () => {
      const RESERVED_PREFIXES = new Set(["sd", "login"]);
      const pathParts = window.location.pathname.split("/").filter(Boolean);
      const fromUrl = pathParts.find(p => !RESERVED_PREFIXES.has(p.toLowerCase())) ?? null;
      if (fromUrl) {
        setTenantIdHandler(fromUrl);
      }
    };
    window.addEventListener("popstate", handleUrlChange);
    return () => window.removeEventListener("popstate", handleUrlChange);
  }, []);

  return (
    <TenantContext.Provider
      value={{
        tenantId,
        setTenantId: setTenantIdHandler,
        clearTenantId,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

// Export the context for use in other files
export { TenantContext };
